import { z } from 'zod';
import type { UserFieldDefLocal } from '../../api/types/TenantLocal';

/** Tope de longitud para campos de texto libre (espeja el límite del backend). */
const TEXT_MAX = 256;

/**
 * Arma el sub-schema zod del bloque `customFields` a partir del catálogo de campos del tenant.
 *
 * Reglas por campo:
 * - `type === 'select'`: el valor debe pertenecer a `options`. Si el campo no es requerido, el
 *   string vacío `''` también se acepta (significa "sin elegir").
 * - `type === 'text'`: string de hasta {@link TEXT_MAX} caracteres.
 * - `required`: además, no puede quedar vacío (mensaje claro). No requerido → opcional.
 *
 * Diseñado para mergear en el schema del form de usuario:
 *   `z.object({ email, displayName, ..., customFields: buildCustomFieldsSchema(fields) })`
 *
 * Si `fields` está vacío devuelve `z.object({})` (un objeto vacío válido), de modo que el merge
 * siempre funcione sin ramas condicionales en el consumidor.
 */
export function buildCustomFieldsSchema(
    fields: UserFieldDefLocal[],
): z.ZodObject<Record<string, z.ZodTypeAny>> {
    const shape: Record<string, z.ZodTypeAny> = {};

    for (const field of fields) {
        let schema: z.ZodTypeAny;

        if (field.type === 'select') {
            const options = field.options ?? [];
            // z.enum exige al menos un literal; si no hay opciones, cae a string validado por refine.
            const inSet = (v: string) => options.includes(v);
            if (field.required) {
                schema = z
                    .string()
                    .min(1, `${field.label} es requerido`)
                    .refine(inSet, `Elige una opción válida para ${field.label}`);
            } else {
                // No requerido: admite '' (sin elegir) o una opción válida.
                schema = z
                    .string()
                    .refine(
                        (v) => v === '' || inSet(v),
                        `Elige una opción válida para ${field.label}`,
                    )
                    .optional();
            }
        } else {
            const base = z
                .string()
                .max(TEXT_MAX, `${field.label} no puede superar ${String(TEXT_MAX)} caracteres`);
            schema = field.required ? base.min(1, `${field.label} es requerido`) : base.optional();
        }

        shape[field.key] = schema;
    }

    return z.object(shape);
}

/**
 * Arma los `defaultValues` del bloque `customFields` para react-hook-form.
 *
 * - En alta (`current` omitido): cada campo arranca en `''`.
 * - En edición: toma el valor de `current[key]` si existe, si no `''`.
 *
 * Devolver siempre un string por campo (controlado, nunca `undefined`) evita el warning de
 * "uncontrolled → controlled" de React en los inputs/selects.
 */
export function pickCustomFieldsDefaults(
    fields: UserFieldDefLocal[],
    current?: Record<string, string>,
): Record<string, string> {
    const defaults: Record<string, string> = {};
    for (const field of fields) {
        defaults[field.key] = current?.[field.key] ?? '';
    }
    return defaults;
}
